// src/components/ChapterCardList.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import CiteModal from "../courseList/components/CiteModal";
import PdfAcrobatIcon from "../courseList/components/PdfAcrobatIcon";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  generateAPA,
  generateMLA,
  generateChicago,
  generateBibTeX,
  generateVancouver,
  generateHarvard,
  generateIEEE
} from "../courseList/components/utils/citationFormats";

/* helpers */

// helpers/language.js (veya component içinde)
const languageNamesInTurkish = {
  tr: "Türkçe",
  en: "İngilizce",
  ar: "Arapça",
  fr: "Fransızca",
  de: "Almanca",
  es: "İspanyolca",
  pt: "Portekizce",
  ru: "Rusça",
  zh: "Çince",
  ja: "Japonca",
  it: "İtalyanca",
  nl: "Hollandaca",
  // gerekiyorsa ekle
  other: "İkinci"
};

function normalizeLangCode(lang) {
  if (!lang) return "";
  return String(lang).split(/[-_]/)[0].toLowerCase(); // 'en-US' -> 'en'
}

/**
 * Eğer ikinci başlık gerçekten varsa, { label, value } döndürür.
 * label örn: "Türkçe Başlık"
 * Eğer ikinci başlık yoksa null döner.
 */
export function getSecondTitleFieldLocalized(pub) {
  // bul: publicationtypeInfo veya templateInfo içindeki "Başlık" attribute'u
  const attr =
    pub.publicationtypeInfo?.find(a => a.attributename === "Başlık") ||
    pub.templateInfo?.find(a => a.attributename === "Başlık");

  if (!attr || !Array.isArray(attr.value)) return null;

  // mevcut mantığı bozulmasın diye sadece 2. elemanı kullanıyoruz
  const secondEntry = attr.value[1]; // burada mevcut mantığınla aynı
  if (!secondEntry || !Array.isArray(secondEntry.value) || !secondEntry.value[0]) return null;

  const lang = normalizeLangCode(secondEntry.lang) || "tr"; // fallback tr
  const langName = languageNamesInTurkish[lang] || languageNamesInTurkish["other"];

  return {
    label: `${langName} Başlık`,
    value: String(secondEntry.value[0]).trim()
  };
}

const pickFrom = (list = [], name, lang = "") => {
  const info = list?.find(a => a?.attributename === name);
  if (!info || !Array.isArray(info.value)) return null;
  const langHit = info.value.find(v => v.lang === lang && Array.isArray(v.value) && v.value[0]);
  const firstHit = info.value.find(v => Array.isArray(v.value) && v.value[0]);
  const raw = (langHit?.value?.[0] ?? firstHit?.value?.[0]) ?? null;
  let result = raw !== null && raw !== undefined ? String(raw).trim() : null;
  if (name === "Başlık" && typeof result === 'string') {
    result = result.replace(/\s*\/$/, '').trim();
  }
  return result;
};
const toInt = (v, d = null) => { const n = parseInt(String(v), 10); return Number.isFinite(n) ? n : d; };

// İkinci başlık helper'ı
const getSecondTitle = (pub) => {
  const t = pub.publicationtypeInfo?.find(attr => attr.attributename === "Başlık")?.value?.[1]?.value?.[0] ||
  pub.templateInfo?.find(attr => attr.attributename === "Başlık")?.value?.[1]?.value?.[0] ||
  "İkinci Başlık Yok";
  return typeof t === 'string' ? t.replace(/\s*\/$/, '').trim() : t;
};


// const getCiltValue = (pub) => {
//   if (!pub) return null;
//   const fromPub = pickFrom(pub.publicationtypeInfo, "Cilt");
//   const fromTemplate = pickFrom(pub.templateInfo, "Cilt");
//   return first(fromPub, fromTemplate) || null;
// };

// const shouldShowCilt = (pub) => {
//   if (!pub || !pub.publicationtype) return false;
//   const t = String(pub.publicationtype).toLowerCase();
//   const targetTypes = [
//     "konferans ve armağan",
//     "konferans ve armagan",
//     "popüler kitap",
//     "populer kitap",
//     "akademik kitap"
//   ];
//   return targetTypes.some(tt => t.includes(tt));
// };

// const first = (...vals) => vals.find(Boolean) || "";

/* HTML -> plain text */
const htmlToPlain = (html) => {
  if (!html) return "";
  const withBreaks = String(html)
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*p\s*>/gi, "\n\n")
    .replace(/<\s*p[^>]*>/gi, "");
  const textOnly = withBreaks.replace(/<[^>]+>/g, "");
  if (typeof window !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = textOnly;
    return (div.textContent || div.innerText || "").trim();
  }
  return textOnly
    .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .trim();
};

/* render authors with links */
const renderAuthors = (authors = []) => {
  if (!Array.isArray(authors) || authors.length === 0) return "";
  return authors.map((p, i) => {
    const full = [p?.name, p?.surname].filter(Boolean).join(" ").trim() || "";
    const href = p?.mysqlId ? `/authors-detail/${p.mysqlId}` : null;
    return (
      <span key={p?.mysqlId || `${full}-${i}`}>
        {href ? (
          <a
            href={href}
            className="text-darkfume"
            style={{ textDecoration: "none", cursor: "pointer" }}
            onMouseOver={e => e.currentTarget.style.textDecoration = "underline"}
            onMouseOut={e => e.currentTarget.style.textDecoration = "none"}
            target="_blank"
            rel="noopener noreferrer"
          >
            {full}
          </a>
        ) : (
          <span className="text-darkfume">{full}</span>
        )}
        {i < authors.length - 1 ? ", " : ""}
      </span>
    );
  });
};

// Chapter için özet alanlarını getiren fonksiyon
const getChapterSummaryFields = (pub) => {
  const info = pub.publicationtypeInfo || [];
  const templateInfo = pub.templateInfo || [];
  const parent0 = pub.parentList?.[0]?.publicationtypeInfo || [];

  // Sadece ikinci başlık gerçekten varsa göster
  const secondTitleField = getSecondTitleFieldLocalized(pub);

  const yearStr = pickFrom(templateInfo, "Yayın Tarihi") || pickFrom(info, "Yayın Tarihi") || pickFrom(parent0, "Yayın Tarihi");
  const yearOnly = yearStr ? String(yearStr).match(/\d{4}/)?.[0] : null;

  const fields = [
    { label: "Yayın Yılı", value: yearOnly },
    { label: "DOI", value: pickFrom(info, "DOI") || pickFrom(parent0, "DOI") },
    { label: "ISBN", value: pickFrom(parent0, "ISBN") },
    { label: "E-ISBN", value: pickFrom(parent0, "E-ISBN") },
    { label: "Web Adresi", value: pickFrom(templateInfo, "Web Adresi") },
    // secondTitleField
  ];

  return fields.filter(x => x && x.value);
};

// Citation format map (PublicationCard ile aynı)
const citationFormats = {
  apa: generateAPA,
  mla: generateMLA,
  chicago: generateChicago,
  vancouver: generateVancouver,
  bibtex: generateBibTeX,
  harvard: generateHarvard,
  ieee: generateIEEE
};

export default function ChapterCardList({ data = [] }) {
  if (!Array.isArray(data) || data.length === 0) return <div className="text-fume">Kayıt yok.</div>;

  const [showModal, setShowModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [openSummaryIds, setOpenSummaryIds] = useState(() => new Set()); // birden çok chapter'ı aç/kapat

  const handleOpenModal = (publication) => {
    setSelectedPublication(publication);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFormat("");
  };
  const renderCitation = () => {
    if (!selectedPublication || !selectedFormat) return "Please select a format.";
    return citationFormats[selectedFormat](selectedPublication);
  };
  const toggleSummary = (id) => {
    setOpenSummaryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const SUMMARY_MAX_PX = 180;

  return (
    <div className="row g-0">
      {data.map((it, idx) => {
        const title = pickFrom(it.publicationtypeInfo, "Başlık") || "Başlık Yok";
        const firstPage = toInt(pickFrom(it.publicationtypeInfo, "İlk Sayfa")) || toInt(pickFrom(it.templateInfo, "İlk Sayfa")) || "";
        const lastPage = toInt(pickFrom(it.publicationtypeInfo, "Son Sayfa")) || toInt(pickFrom(it.templateInfo, "Son Sayfa")) || "";
        const summaryPlain = htmlToPlain(pickFrom(it.publicationtypeInfo, "Özet"));
        const isSummaryOpen = openSummaryIds.has(it.mysqlId);
        const pdfLink = it.pdffile
          ? (it.openaccess === 1
            ? `/view-pdf/${it.mysqlId}`
            : `/view-pdf-protectedaccess/${it.mysqlId}`)
          : null;

        // Chapter için özet alanları
        const summaryFields = getChapterSummaryFields(it);

        return (
          <div className="col-12" key={it.mysqlId}>
            <div className="card border-0 rounded-3 shadow-sm bg-default-color-3">
              <div className="card-body p-3">
                {/* üst: tür solda, bölüm sağda */}
                <div className="d-flex justify-content-between align-items-start mb-2 ms-2">
                  <span className="badge bg-light text-darkfume border" style={{ borderColor: "#e0e2f5" }}>
                    {/* {it.publicationtype || "Bölüm"} */}
                  </span>
                  {/* <span className="small text-darkfume text-xs me-2">Bölüm {idx + 1}</span> */}
                </div>

                {/* başlık */}
                <div className="mb-1 ms-2" style={{ lineHeight: 1.3 }}>
                  {pdfLink ? (
                    <Link
                      to={pdfLink}
                      className="text-newdarkfume fs-6 text-decoration-none"
                      title={title}
                    >
                      {title}
                    </Link>
                  ) : (
                    <span className="text-newdarkfume fs-6" title={title}>
                      {title}
                    </span>
                  )}

                  {/* show (Cilt X) when applicable */}
                  {/* {(() => {
                    const cilt = getCiltValue(it);
                    const show = shouldShowCilt(it) && Boolean(cilt);
                    return show ? (
                      <span className="ms-1 text-fume" style={{ fontSize: "0.90rem" }}>
                        ({`Cilt ${cilt}`})
                      </span>
                    ) : null;
                  })()} */}

                </div>

                {/* yazarlar */}
                <div className="ms-2 text-xs" style={{ fontSize: "0.95rem" }}>
                  {renderAuthors(it.authors)}
                </div>

                {/* sayfa */}
                <div className="text-darkfume text-xs ms-2" style={{ fontSize: "0.95rem" }}>
                  {firstPage ? <>Sayfa: {firstPage}{lastPage && lastPage !== firstPage ? `-${lastPage}` : ""}</> : <span className="fst-italic">Sayfa bilgisi yok</span>}
                </div>

                {/* --- PublicationCard'daki Aksiyon Barı: Özet | Alıntıla | PDF --- */}
                <div className="d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between gap-2 py-1 ps-1 mt-2">
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn-sm text-blue border-0 bg-transparent"
                      style={{ fontSize: "0.80rem" }}
                      onClick={() => toggleSummary(it.mysqlId)}
                      aria-expanded={isSummaryOpen}
                    >
                      Özet<span style={{ verticalAlign: 'top', marginLeft: 2 }}>{isSummaryOpen ? '▲' : '▼'}</span>
                    </button>


                    {it.publicationtype !== "Popüler Kitap Bölümü" && (
                      <button
                        className="btn-sm text-blue border-0 bg-transparent"
                        style={{ fontSize: "0.80rem" }}
                        onClick={() => handleOpenModal(it)}
                      >
                        Alıntıla
                      </button>
                    )}

                    {pdfLink && (
                      <OverlayTrigger
                        placement="right"
                        delay={{ show: 150, hide: 0 }}
                        overlay={
                          <Tooltip id={`pdf-tip-${it.mysqlId}`} style={{ fontSize: "0.78rem", lineHeight: 1.1 }}>
                            PDF'i görüntüle
                          </Tooltip>
                        }
                      >
                        <button
                          className="btn-sm border-0 bg-transparent d-inline-flex align-items-center"
                          style={{ fontSize: "0.80rem", color: "#cb1407ff" }}
                          onClick={() => window.open(pdfLink, "_blank", "noopener,noreferrer")}
                          title="PDF'i görüntüle"
                          aria-label="PDF'i görüntüle"
                        >
                          <PdfAcrobatIcon size={16} />
                        </button>
                      </OverlayTrigger>
                    )}

                  </div>

                  {/* sağ tarafta istersek başka aksiyonlar olabilir; boş bırakıyoruz */}
                  <div />
                </div>

                {/* --- PublicationCard ile aynı ÖZET PANELİ --- */}
                {isSummaryOpen && (
                  <div
                    className="journal-info-anim open"
                    style={{
                      borderRadius: "0.75rem",
                      padding: "1rem",
                      fontSize: '0.95em',
                      marginTop: "0.25rem",
                    }}
                  >
                    <strong className="ms-1">Özet:</strong>
                    <div
                      className="mb-2 mt-1 p-2 rounded-3 border"
                      style={{
                        maxHeight: SUMMARY_MAX_PX,
                        overflowY: "auto",
                        lineHeight: 1.4,
                        scrollbarWidth: "thin",
                      }}
                    >
                      <div className="text-darkfume">
                        {summaryPlain || "Bilgi yok."}
                      </div>
                    </div>

                    {/* Chapter özelinde ekstra alanlar */}
                    {summaryFields.length > 0 && (
                      <div className="row small">
                        {summaryFields.map((f, i) => (
                          <div className="col-md-6 mb-1" key={i}>
                            <b>{f.label}:</b>{" "}
                            {String(f.value).startsWith("doi.org/") || String(f.value).startsWith("http") ? (
                              <a href={`https://${String(f.value).replace(/^https?:\/\//, '')}`} target="_blank" rel="noopener noreferrer">
                                {f.value}
                              </a>
                            ) : (
                              <span>{f.value}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Alıntılama Modal (PublicationCard ile aynı props yapısı) */}
      <CiteModal
        show={showModal}
        onHide={handleCloseModal}
        selectedFormat={selectedFormat}
        setSelectedFormat={setSelectedFormat}
        renderCitation={renderCitation}
      />
    </div>
  );
}